import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, PauseCircle, RefreshCw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import {
  GraphLibraryType,
  GraphLibraryTypeLabels,
  GraphLibraryVersioningStatus,
  MAJOR_VERSION_LIMIT_ON_ENABLE,
  VersioningCheckSourceLabels,
  VersioningState,
  VersioningStateLabels,
  isVersioningTargetUnconfigured,
  parseVersioningOverrides,
  serializeVersioningOverrides,
  versioningObservedOn,
} from "../../models/ClientConfiguration/VersioningState";
import GraphLibrariesRepository from "../../repositories/GraphLibrariesRepository";
import { convertUtcToLocal, formatRelativeToNow } from "../../helpers/CommonHelpers";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import ActionConfirmationModal from "../common/ActionConfirmationModal";
import VersioningStatusChip from "../common/VersioningStatusChip";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

/**
 * A versioning change waiting to be confirmed.
 *
 * One shape covers a single target and a whole-configuration sweep, because they ask the operator the
 * same question about the same tenant and only differ in how many libraries are listed.
 */
interface PendingVersioningChange {
  libraryTypes: GraphLibraryType[];
  enableVersioning: boolean;
}

/** Root drive field each library type takes its configured name from. */
const driveNameFields: Record<GraphLibraryType, keyof ClientConfiguration> = {
  [GraphLibraryType.CaseFiles]: "casesSharepointRootDriveName",
  [GraphLibraryType.ProviderFiles]: "providersSharepointRootDriveName",
  [GraphLibraryType.FirmDocuments]: "firmDocumentsSharepointRootDriveName",
  [GraphLibraryType.TemplateFiles]: "templateFilesSharepointRootDriveName",
};

const libraryTypes = Object.values(GraphLibraryType);

interface LibraryVersioningPanelProps {
  organizationId: string;
  clientConfigurationId?: string | null;
  disabled?: boolean;
}

/**
 * Versioning of the four configured root drives, as one table.
 *
 * The two controls sit in separate columns because they behave differently: a check hits SharePoint
 * the moment it is clicked, while an override is form state that does nothing until the configuration
 * is saved. Presenting them as neighbouring widgets invites an operator to believe a toggle took
 * effect.
 */
const LibraryVersioningPanel: React.FC<LibraryVersioningPanelProps> = ({
  organizationId,
  clientConfigurationId,
  disabled = false,
}) => {
  const form = useFormContext<ClientConfiguration>();
  const graphLibrariesRepository = new GraphLibrariesRepository();
  const queryClient = useQueryClient();

  // An unsaved configuration has no id yet, so no library can have been registered against it.
  const versioningQuery = useQuery({
    queryKey: [QUERY_KEYS.GRAPH_LIBRARY_VERSIONING, clientConfigurationId],
    queryFn: async () => await graphLibrariesRepository.getVersioningStatus(clientConfigurationId!),
    enabled: !!clientConfigurationId,
  });

  const invalidateVersioning = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GRAPH_LIBRARY_VERSIONING, clientConfigurationId],
      }),
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_GRAPH_LIBRARY_VERSIONING, organizationId],
      }),
    ]);

  const checkVersioningMutation = useMutation({
    mutationFn: async (libraryType: GraphLibraryType) =>
      await graphLibrariesRepository.checkVersioning(clientConfigurationId!, libraryType),
    onSuccess: (result) => {
      // The two outcomes are not interchangeable: one is a reading just taken, the other is the
      // state already on record, so the wording must not suggest it was refreshed.
      if (result.wasReadDirectly) {
        toast.success(`Checked: ${VersioningStateLabels[result.status.versioningState]}.`);
      } else {
        // The server reports what actually blocked the read — a missing tenant id and a missing
        // certificate need different fixes, and naming the wrong one sends an operator hunting.
        toast.info(
          result.unavailableReason ??
            "The server could not read versioning for this library. The client establishes it on its own schedule.",
        );
      }
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => invalidateVersioning(),
  });

  const checkAllMutation = useMutation({
    mutationFn: async (types: GraphLibraryType[]) => {
      // Sequential rather than concurrent: four parallel reads of one site invite SharePoint
      // throttling, and a throttled read reports as CheckFailed, which looks like a real fault.
      //
      // One target failing does not abandon the others — they are separate libraries, and stopping
      // partway leaves the operator unable to tell which of them were looked at.
      let read = 0;
      let notRead = 0;
      const failures: string[] = [];

      for (const libraryType of types) {
        try {
          const result = await graphLibrariesRepository.checkVersioning(
            clientConfigurationId!,
            libraryType,
          );
          if (result.wasReadDirectly) read += 1;
          else notRead += 1;
        } catch (error) {
          failures.push(
            `${GraphLibraryTypeLabels[libraryType]}: ${(error as Error).message}`,
          );
        }
      }

      return { read, notRead, failures };
    },
    // Reports what actually happened, target by target. The single-target check draws the same
    // distinction, and for the same reason: a reading just taken is not the state already on record,
    // so a count of attempts would claim refreshes that never occurred.
    onSuccess: ({ read, notRead, failures }) => {
      const summary = [
        `${read} read from SharePoint`,
        notRead > 0 ? `${notRead} not readable` : null,
        failures.length > 0 ? `${failures.length} failed` : null,
      ]
        .filter(Boolean)
        .join(", ");

      if (failures.length > 0) toast.error(summary, { description: failures.join("\n") });
      else if (read === 0) toast.info(summary);
      else toast.success(summary);
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => invalidateVersioning(),
  });

  const [pendingChange, setPendingChange] = useState<PendingVersioningChange | null>(null);

  const setVersioningMutation = useMutation({
    mutationFn: async ({ libraryTypes, enableVersioning }: PendingVersioningChange) => {
      const expected = enableVersioning ? VersioningState.Enabled : VersioningState.Disabled;
      const applied: string[] = [];
      const unconfirmed: string[] = [];
      const failures: string[] = [];

      // Sequential, and for the same reason the checks are: several writes against one site invite
      // SharePoint throttling, and a throttled write is refused in a way that reads like a real
      // rejection. One target failing does not abandon the others — they are separate libraries, and
      // stopping partway leaves the operator unable to tell which of them were touched.
      for (const libraryType of libraryTypes) {
        const label = GraphLibraryTypeLabels[libraryType];

        try {
          const status = await graphLibrariesRepository.setVersioning(
            clientConfigurationId!,
            libraryType,
            enableVersioning,
          );

          if (status.versioningState === expected) applied.push(label);
          else unconfirmed.push(`${label}: reads ${VersioningStateLabels[status.versioningState]}`);
        } catch (error) {
          failures.push(`${label}: ${(error as Error).message}`);
        }
      }

      return { applied, unconfirmed, failures, enableVersioning };
    },
    // Three outcomes, reported separately. A write SharePoint accepted whose read-back did not come
    // back as the state asked for is neither a success nor a refusal: the change went in and what is
    // now in force was not established, so calling it done would be a claim nothing supports.
    onSuccess: ({ applied, unconfirmed, failures, enableVersioning }) => {
      if (failures.length > 0) {
        toast.error(`${failures.length} ${failures.length === 1 ? "change" : "changes"} failed`, {
          description: failures.join("\n"),
          duration: 10000,
        });
      }

      if (unconfirmed.length > 0) {
        toast.warning("Change accepted but not confirmed", {
          description:
            `${unconfirmed.join("\n")}\nSharePoint took the change; check again to establish the ` +
            "setting now in force.",
          duration: 10000,
        });
      }

      if (applied.length > 0) {
        toast.success(
          `Versioning turned ${enableVersioning ? "on" : "off"} for ${applied.join(", ")}`,
        );
      }
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => invalidateVersioning(),
  });

  const statuses = versioningQuery.data?.libraries ?? [];
  const serverCanRead = versioningQuery.data?.serverCanRead ?? false;
  const serverUnavailableReason = versioningQuery.data?.serverUnavailableReason;
  const rootSiteName = versioningQuery.data?.sharepointRootSiteName?.trim();

  const statusFor = (libraryType: GraphLibraryType) =>
    statuses.find((status) => status.libraryType === libraryType);

  const overrides = parseVersioningOverrides(form.watch("versioningEnforcementOverrides"));

  const toggleOverride = (libraryType: GraphLibraryType, enabled: boolean) => {
    const next = enabled
      ? [...overrides, libraryType]
      : overrides.filter((entry) => entry !== libraryType);
    form.setValue("versioningEnforcementOverrides", serializeVersioningOverrides(next), {
      shouldDirty: true,
    });
  };

  /** A check reads nothing without a certificate, and nothing resolves without a registered library. */
  const canCheck = (status?: GraphLibraryVersioningStatus) =>
    !!clientConfigurationId &&
    serverCanRead &&
    !!status &&
    status.versioningState !== VersioningState.LibraryNotFound;

  const checkableTypes = libraryTypes.filter((libraryType) => canCheck(statusFor(libraryType)));

  /**
   * Why this library's versioning cannot be changed from here, or null when it can.
   *
   * A reason rather than a bare no, because the control stays on screen and disabled: an operator who
   * cannot see the action at all has no way to tell an unavailable one from one that does not exist,
   * and the four causes here need four different remedies.
   */
  const versioningChangeBlockedReason = (status?: GraphLibraryVersioningStatus): string | null => {
    if (!clientConfigurationId)
      return "Save this configuration before changing versioning.";

    // A change needs the same certificate a read does — one token serves both.
    if (!serverCanRead)
      return (
        serverUnavailableReason ??
        "No certificate is configured for this SharePoint app registration."
      );

    if (!status || isVersioningTargetUnconfigured(status))
      return "No root drive name is set for this target, so there is no library to change.";

    // There is no "off" to switch on when the current setting was never established, and writing one
    // anyway would be an unexplained change to the firm's tenant that may not even alter anything.
    if (
      status.versioningState !== VersioningState.Enabled &&
      status.versioningState !== VersioningState.Disabled
    )
      return (
        "Versioning is not established for this library. Check it first — its current setting is what " +
        "decides which change is offered."
      );

    return null;
  };

  const canChangeVersioning = (status?: GraphLibraryVersioningStatus) =>
    versioningChangeBlockedReason(status) === null;

  // Only libraries whose versioning is on have anything to switch off, so they are the sweep's targets.
  const versionedTypes = libraryTypes.filter((libraryType) => {
    const status = statusFor(libraryType);
    return status?.versioningState === VersioningState.Enabled && canChangeVersioning(status);
  });

  const isWriting = setVersioningMutation.isPending;
  const isChecking = checkVersioningMutation.isPending || checkAllMutation.isPending;
  const checkingType = checkVersioningMutation.isPending
    ? checkVersioningMutation.variables
    : undefined;

  // Only unoverridden libraries are called out: a versioned library with an override in force is
  // migrating, so listing it as blocked would be wrong.
  const blockedLabels = libraryTypes
    .filter((libraryType) => {
      const status = statusFor(libraryType);
      return (
        status?.versioningState === VersioningState.Enabled && !overrides.includes(libraryType)
      );
    })
    .map((libraryType) => GraphLibraryTypeLabels[libraryType]);

  const heading = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h5 className="text-sm font-semibold flex items-center gap-1">Document library versioning<DocsHelpLink to="configuration/library-versioning" className="size-6" /></h5>
        <p className="text-xs text-muted-foreground">
          Read from SharePoint by the server, or reported by a client.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {versionedTypes.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || isChecking || isWriting}
            onClick={() =>
              setPendingChange({ libraryTypes: versionedTypes, enableVersioning: false })
            }
          >
            Turn versioning off for all
          </Button>
        )}
        {checkableTypes.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || isChecking || isWriting}
            onClick={() => checkAllMutation.mutate(checkableTypes)}
          >
            <RefreshCw className={checkAllMutation.isPending ? "animate-spin" : ""} />
            Check all
          </Button>
        )}
      </div>
    </div>
  );

  const renderBody = () => {
    if (!clientConfigurationId) {
      return (
        <p className="text-xs text-muted-foreground">
          Versioning appears once this configuration has been saved and a client has registered its
          libraries.
        </p>
      );
    }

    if (versioningQuery.isLoading) {
      return (
        <div className="flex flex-col gap-2">
          {libraryTypes.map((libraryType) => (
            <Skeleton key={libraryType} className="h-9 w-full" />
          ))}
        </div>
      );
    }

    if (versioningQuery.isError) {
      return (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertTitle>Versioning status could not be loaded</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            <span>{(versioningQuery.error as Error)?.message}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => versioningQuery.refetch()}
            >
              <RefreshCw />
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <>
        {blockedLabels.length > 0 && (
          <Alert className="border-amber-500/50 text-amber-900 dark:text-amber-200 [&>svg]:text-amber-600">
            <TriangleAlert />
            <AlertTitle>
              Versioning is on for {blockedLabels.join(", ")}
            </AlertTitle>
            <AlertDescription className="text-amber-900/90 dark:text-amber-200/90">
              Migration into {blockedLabels.length === 1 ? "that target" : "those targets"} is held
              until versioning is turned off in SharePoint, or an override below is enabled and saved.
            </AlertDescription>
          </Alert>
        )}

        {!serverCanRead && (
          <Alert>
            <Info />
            <AlertTitle>The server cannot read versioning for this configuration</AlertTitle>
            <AlertDescription>
              {serverUnavailableReason ??
                "No certificate is configured for this SharePoint app registration."}{" "}
              Status arrives only when a client reports it; there is nothing to refresh from here.
            </AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              <TableHead>Library</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last checked</TableHead>
              <TableHead>Migration</TableHead>
              <TableHead>
                Override
                <span className="block font-normal text-muted-foreground">applies on save</span>
              </TableHead>
              <TableHead>
                Versioning
                <span className="block font-normal text-muted-foreground">changes SharePoint now</span>
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {libraryTypes.map((libraryType) => (
              <VersioningRow
                key={libraryType}
                libraryType={libraryType}
                status={statusFor(libraryType)}
                currentDriveName={form.watch(driveNameFields[libraryType] as any) as string | null}
                isOverridden={overrides.includes(libraryType)}
                onToggleOverride={(enabled) => toggleOverride(libraryType, enabled)}
                onCheck={() => checkVersioningMutation.mutate(libraryType)}
                canCheck={canCheck(statusFor(libraryType))}
                isCheckingThis={checkingType === libraryType || checkAllMutation.isPending}
                isAnyCheckRunning={isChecking}
                changeBlockedReason={versioningChangeBlockedReason(statusFor(libraryType))}
                onChangeVersioning={(enableVersioning) =>
                  setPendingChange({ libraryTypes: [libraryType], enableVersioning })
                }
                isWriting={isWriting}
                disabled={disabled}
              />
            ))}
          </TableBody>
        </Table>
      </>
    );
  };

  /**
   * Names every library the change would land on, so the operator confirms against SharePoint's own
   * names rather than the portal's labels for them. Where a resolved name and a configured name have
   * diverged it is the resolved one that identifies what will actually be written to.
   */
  const renderPendingTargets = (change: PendingVersioningChange) => (
    <ul className="list-disc space-y-1 pl-4">
      {change.libraryTypes.map((libraryType) => {
        const status = statusFor(libraryType);
        const libraryName = status?.libraryName || status?.configuredDriveName;

        return (
          <li key={libraryType}>
            <span className="font-medium">{GraphLibraryTypeLabels[libraryType]}</span>
            {libraryName ? ` — ${libraryName}` : ""}
            {rootSiteName ? ` on ${rootSiteName}` : ""}
          </li>
        );
      })}
    </ul>
  );

  // Each consumer establishes its own provider in this codebase; there is no global one.
  return (
    <TooltipProvider>
      <div className="mt-6 flex flex-col gap-3">
        <Separator />
        {heading}
        {renderBody()}
      </div>

      {pendingChange && (
        <ActionConfirmationModal
          open
          onClose={() => setPendingChange(null)}
          // Nothing here is reversed by abandoning a form, so there is no draft to explain; the dialog
          // exists to name what is about to change in the firm's tenant, not to collect a justification.
          requireReason={false}
          actionConfig={{
            title: pendingChange.enableVersioning
              ? "Turn versioning on in SharePoint"
              : "Turn versioning off in SharePoint",
            reasonLabel: "",
            reasonPlaceholder: "",
            confirmButtonText: pendingChange.enableVersioning ? "Turn on" : "Turn off",
            confirmButtonColor: pendingChange.enableVersioning ? "warning" : "primary",
          }}
          description={
            <div className="space-y-2 text-sm">
              <p>
                This takes effect in the firm's SharePoint tenant immediately, for{" "}
                {pendingChange.libraryTypes.length === 1
                  ? "this library"
                  : `these ${pendingChange.libraryTypes.length} libraries`}
                :
              </p>
              {renderPendingTargets(pendingChange)}
            </div>
          }
          warnings={
            pendingChange.enableVersioning
              ? [
                  `SharePoint will keep up to ${MAJOR_VERSION_LIMIT_ON_ENABLE} versions of every file in ` +
                    `${pendingChange.libraryTypes.length === 1 ? "this library" : "these libraries"}.`,
                  "Clients hold migration into a library whose versioning is on, unless an override is " +
                    "enabled and saved for that target.",
                ]
              : [
                  "Version history already kept is not removed — SharePoint stops adding to it.",
                  "Migration resumes when a client next checks the library. Until then this panel keeps " +
                    "showing the migration state a client last reported.",
                ]
          }
          onConfirm={async () => {
            await setVersioningMutation.mutateAsync(pendingChange);
          }}
        />
      )}
    </TooltipProvider>
  );
};

interface VersioningRowProps {
  libraryType: GraphLibraryType;
  status?: GraphLibraryVersioningStatus;
  currentDriveName?: string | null;
  isOverridden: boolean;
  onToggleOverride: (enabled: boolean) => void;
  onCheck: () => void;
  canCheck: boolean;
  isCheckingThis: boolean;
  isAnyCheckRunning: boolean;
  /** Null when the change is available; otherwise what to tell the operator instead. */
  changeBlockedReason: string | null;
  onChangeVersioning: (enableVersioning: boolean) => void;
  isWriting: boolean;
  disabled: boolean;
}

const VersioningRow: React.FC<VersioningRowProps> = ({
  libraryType,
  status,
  currentDriveName,
  isOverridden,
  onToggleOverride,
  onCheck,
  canCheck,
  isCheckingThis,
  isAnyCheckRunning,
  changeBlockedReason,
  onChangeVersioning,
  isWriting,
  disabled,
}) => {
  const unconfigured = !!status && isVersioningTargetUnconfigured(status);
  const observedOn = status ? versioningObservedOn(status) : null;
  const versioningIsOn = status?.versioningState === VersioningState.Enabled;

  // Named after the change it would make, and only where the current setting is established. "Change"
  // stands in where it is not: a disabled "Turn off" would assert versioning is on when nothing knows.
  const changeLabel = versioningIsOn
    ? "Turn off"
    : status?.versioningState === VersioningState.Disabled
      ? "Turn on"
      : "Change";

  // The status was resolved against the saved name, so an edited field describes a different library
  // than the row does until the configuration is saved and a client re-registers.
  const savedDriveName = status?.configuredDriveName?.trim() ?? "";
  const editedDriveName = currentDriveName?.trim() ?? "";
  const hasUnsavedRename = !!editedDriveName && editedDriveName !== savedDriveName;

  // What a client registers is the library's URL path segment, which drifts from the configured name
  // on rename, on escaping, and for a site's default library. The row resolved anyway — on site and
  // type — so naming both makes an otherwise invisible mismatch actionable.
  const resolvedName = status?.libraryName?.trim() ?? "";
  const resolvedNameDiffers =
    !!resolvedName &&
    !!savedDriveName &&
    resolvedName.toLowerCase() !== savedDriveName.toLowerCase();

  return (
    <TableRow>
      <TableCell className="font-medium">{GraphLibraryTypeLabels[libraryType]}</TableCell>

      <TableCell>
        <span className="text-sm">
          {status?.libraryName || status?.configuredDriveName || "—"}
        </span>
        {resolvedNameDiffers && (
          <span className="block text-xs text-amber-700 dark:text-amber-300">
            configured: {savedDriveName}
          </span>
        )}
        {hasUnsavedRename && (
          <span className="block text-xs text-muted-foreground">
            unsaved: {editedDriveName}
          </span>
        )}
      </TableCell>

      <TableCell>
        {!status ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : unconfigured ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="neutral">Not configured</Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              No root drive name is set for this target, so there is no library to report on. This is
              not a missing library.
            </TooltipContent>
          </Tooltip>
        ) : (
          <>
            <VersioningStatusChip status={status} />
            {status.versioningState === VersioningState.Enabled && (
              <span className="block text-xs text-muted-foreground">
                {status.majorVersionLimit != null
                  ? `keeps up to ${status.majorVersionLimit} versions`
                  : "keeps unlimited versions"}
                {status.minorVersionsEnabled === true && " · minor versions on"}
              </span>
            )}
          </>
        )}
      </TableCell>

      <TableCell>
        {observedOn ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm">{formatRelativeToNow(observedOn)}</span>
            </TooltipTrigger>
            <TooltipContent>{convertUtcToLocal(observedOn)}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-sm text-muted-foreground">Never</span>
        )}
        {status && !unconfigured && (
          <span className="block text-xs text-muted-foreground">
            {VersioningCheckSourceLabels[status.checkSource]}
          </span>
        )}
      </TableCell>

      <TableCell>
        <MigrationCell status={status} unconfigured={unconfigured} />
      </TableCell>

      <TableCell>
        <Switch
          checked={isOverridden}
          disabled={disabled || unconfigured}
          onCheckedChange={(checked) => onToggleOverride(checked === true)}
          aria-label={`Migrate into ${GraphLibraryTypeLabels[libraryType]} even when versioning is on`}
        />
        {isOverridden && (
          <span className="block text-xs text-muted-foreground">
            migrates despite versioning
          </span>
        )}
      </TableCell>

      {/*
        A labelled button rather than a second switch. The override beside it is form state that does
        nothing until the configuration is saved, and a switch here would read as more of the same while
        in fact writing to the firm's SharePoint tenant on click.
      */}
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            {/*
              A disabled button fires no pointer events of its own, so the tooltip that explains why it
              is disabled has to be triggered by something around it.
            */}
            <span className="inline-block">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!!changeBlockedReason || disabled || isAnyCheckRunning || isWriting}
                onClick={() => onChangeVersioning(!versioningIsOn)}
              >
                {changeLabel}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {changeBlockedReason ??
              `Turn versioning ${versioningIsOn ? "off" : "on"} for this library in SharePoint`}
          </TooltipContent>
        </Tooltip>
      </TableCell>

      <TableCell>
        {canCheck && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                disabled={disabled || isAnyCheckRunning}
                onClick={onCheck}
                aria-label={`Check ${GraphLibraryTypeLabels[libraryType]} versioning now`}
              >
                <RefreshCw className={isCheckingThis ? "animate-spin" : ""} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Read this library's versioning from SharePoint now</TooltipContent>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

/**
 * Whether files are moving into this library.
 *
 * Three-valued on purpose. A server-side read establishes the versioning setting but never touches
 * suspension, so a null here means no client has said either way — rendering that as "allowed" would
 * claim work is flowing on no evidence at all.
 */
const MigrationCell: React.FC<{
  status?: GraphLibraryVersioningStatus;
  unconfigured: boolean;
}> = ({ status, unconfigured }) => {
  if (!status || unconfigured) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  if (status.migrationSuspended === true) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-destructive">
        <PauseCircle className="size-3.5" />
        Suspended
      </span>
    );
  }

  if (status.migrationSuspended === false) {
    return (
      <span className="text-sm">
        Allowed
        {status.overrideApplied === true && (
          <span className="block text-xs text-muted-foreground">under override</span>
        )}
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-sm text-muted-foreground">Unreported</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        No client has reported whether it is holding uploads to this library.
      </TooltipContent>
    </Tooltip>
  );
};

export default LibraryVersioningPanel;
